import type { Drill, Hint } from "./types.ts";
import { HARNESS_KIT } from "./harness-kit.ts";
import { FAKE_CLOCK_PRELUDE } from "./fake-clock.ts";

const starter = `type RetryOptions = {
  attempts: number;            // total tries, including the first
  baseDelayMs: number;         // delay before retry 1
  isRetryable?: (error: unknown) => boolean;   // default: everything is
};

async function retry<T>(operation: () => Promise<T>, options: RetryOptions): Promise<T> {
  // TODO
  return operation();
}
`;

const solution = `type RetryOptions = {
  attempts: number;
  baseDelayMs: number;
  isRetryable?: (error: unknown) => boolean;
};

const sleep = (ms: number) => new Promise((resolve) => setTimeout(resolve, ms));

async function retry<T>(operation: () => Promise<T>, options: RetryOptions): Promise<T> {
  const { attempts, baseDelayMs, isRetryable = () => true } = options;
  if (attempts < 1) throw new Error("attempts must be at least 1");

  let lastError: unknown;
  for (let attempt = 1; attempt <= attempts; attempt++) {
    try {
      return await operation();
    } catch (error) {
      lastError = error;
      // Two separate reasons to stop, and conflating them is the usual bug.
      // A 400 will fail identically forever, so retrying it just delays the
      // error the caller needs to see.
      if (!isRetryable(error)) throw error;
      if (attempt === attempts) break;
      // Doubling from the base: 1x, 2x, 4x. The exponent is attempt-1, so the
      // FIRST retry waits baseDelayMs rather than twice it.
      await sleep(baseDelayMs * 2 ** (attempt - 1));
    }
  }
  throw lastError;
}`;

const harness = HARNESS_KIT + `
if (typeof retry !== "function") {
  throw new Error("retry is not defined. Keep the function name as given.");
}

// An operation that fails a given number of times, then succeeds. Records the
// virtual time of every call, so the delay schedule can be asserted exactly.
function __flaky(failures, value) {
  const f = { calls: 0, at: [] };
  f.op = function () {
    f.calls++;
    f.at.push(__clock.now);
    if (f.calls <= failures) return Promise.reject(new Error("fail " + f.calls));
    return Promise.resolve(value === undefined ? "ok" : value);
  };
  return f;
}

const __tests = [
  {
    name: "returns the value when the operation succeeds first time",
    about: "No failure means no retry and no delay.",
    run: async function () {
      const f = __flaky(0, "done");
      const r = await __drain(retry(f.op, { attempts: 3, baseDelayMs: 100 }));
      __assert("retry(succeeds immediately)", r.ok && r.value === "done",
        ["expected it to resolve with 'done'", "but got " + __show(r)]);
      __expectExact("... call count", function () { return f.calls; }, 1);
    }
  },
  {
    name: "retries a failing operation until it succeeds",
    about: "The basic contract: transient failures should not reach the caller.",
    run: async function () {
      const f = __flaky(2, "recovered");
      const r = await __drain(retry(f.op, { attempts: 5, baseDelayMs: 100 }));
      __assert("fails twice then succeeds", r.ok && r.value === "recovered",
        ["expected it to resolve with 'recovered'", "but got " + __show(r)]);
      __expectExact("... call count", function () { return f.calls; }, 3);
    }
  },
  {
    name: "rejects once the attempts are used up",
    about: "Retrying forever turns a broken dependency into a hung request.",
    run: async function () {
      const f = __flaky(99);
      const r = await __drain(retry(f.op, { attempts: 3, baseDelayMs: 10 }));
      __assert("always fails, attempts 3", !r.ok, ["expected it to reject", "but it resolved"]);
    }
  },
  {
    name: "attempts means total tries, not extra tries",
    hidden: true,
    about: "The off-by-one everyone hits. attempts: 3 is one initial call plus two retries, not four calls.",
    run: async function () {
      const f = __flaky(99);
      await __drain(retry(f.op, { attempts: 3, baseDelayMs: 10 }));
      __expectExact("attempts: 3 against an always-failing operation", function () { return f.calls; }, 3);
    }
  },
  {
    name: "the first retry waits the base delay, not double it",
    hidden: true,
    about: "The exponent is attempt-1. Using attempt itself makes every wait twice as long as configured, which nothing catches except a test on the timing.",
    run: async function () {
      const f = __flaky(1, "ok");
      await __drain(retry(f.op, { attempts: 3, baseDelayMs: 100 }));
      __expectExact("first call at t=0, retry at", function () { return f.at; }, [0, 100]);
    }
  },
  {
    name: "delays double each time",
    hidden: true,
    about: "Exponential, not fixed. A constant delay against a struggling service is a stampede.",
    run: async function () {
      const f = __flaky(3, "ok");
      await __drain(retry(f.op, { attempts: 5, baseDelayMs: 100 }));
      __expectExact("call times with base 100", function () { return f.at; }, [0, 100, 300, 700]);
    }
  },
  {
    name: "a non-retryable error is thrown immediately",
    hidden: true,
    about: "A 400 fails the same way forever. Retrying it wastes the budget and delays the error the caller needs. This is the bug that turns a bad request into a slow outage.",
    run: async function () {
      const f = { calls: 0 };
      f.op = function () {
        f.calls++;
        const e = new Error("bad request");
        e.status = 400;
        return Promise.reject(e);
      };
      const r = await __drain(retry(f.op, {
        attempts: 5,
        baseDelayMs: 10,
        isRetryable: function (e) { return e.status >= 500; }
      }));
      __assert("isRetryable rejects a 400", !r.ok, ["expected it to reject", "but it resolved"]);
      __expectExact("... call count", function () { return f.calls; }, 1);
    }
  },
  {
    name: "a retryable error is still retried when isRetryable is given",
    hidden: true,
    about: "The predicate has to let things through as well as block them.",
    run: async function () {
      const f = { calls: 0 };
      f.op = function () {
        f.calls++;
        if (f.calls === 1) { const e = new Error("boom"); e.status = 503; return Promise.reject(e); }
        return Promise.resolve("ok");
      };
      const r = await __drain(retry(f.op, {
        attempts: 3,
        baseDelayMs: 10,
        isRetryable: function (e) { return e.status >= 500; }
      }));
      __assert("isRetryable allows a 503", r.ok && r.value === "ok",
        ["expected it to resolve with 'ok'", "but got " + __show(r)]);
      __expectExact("... call count", function () { return f.calls; }, 2);
    }
  },
  {
    name: "the last error is the one thrown",
    hidden: true,
    about: "Throwing a generic 'retries exhausted' discards the diagnosis. The caller needs the real failure.",
    run: async function () {
      const f = { calls: 0 };
      f.op = function () { f.calls++; return Promise.reject(new Error("attempt " + f.calls)); };
      const r = await __drain(retry(f.op, { attempts: 3, baseDelayMs: 10 }));
      __assert("three failures, last error", !r.ok && r.error.indexOf("attempt 3") !== -1,
        ["expected the error from the final attempt ('attempt 3')", "but got " + __show(r.error)]);
    }
  },
  {
    name: "attempts of 1 never retries",
    hidden: true,
    about: "One attempt means try once. A loop written as do/while with the guard at the end retries anyway.",
    run: async function () {
      const f = __flaky(99);
      await __drain(retry(f.op, { attempts: 1, baseDelayMs: 10 }));
      __expectExact("attempts: 1", function () { return f.calls; }, 1);
    }
  },
  {
    name: "no delay is paid after the final failure",
    hidden: true,
    about: "Sleeping after the last attempt makes every failing call slower than it needs to be, for a retry that never happens.",
    run: async function () {
      const f = __flaky(99);
      await __drain(retry(f.op, { attempts: 3, baseDelayMs: 100 }));
      __expectExact("attempts 3, base 100: time of last call", function () { return f.at[2]; }, 300);
      __assert("no trailing sleep", __clock.now === 300,
        ["the clock should stop at the last attempt (300)", "but it reached " + __clock.now]);
    }
  }
];
`;

const hints: Hint[] = [
  {
    label: "Nudge",
    body: "Not every failure is worth retrying. Ask what should happen when the error is one that will fail in exactly the same way every time. There are two separate reasons to stop, and conflating them is the usual bug.",
    code: `type RetryOptions = {
  attempts: number;          // TOTAL tries, including the first
  baseDelayMs: number;
  isRetryable?: (error: unknown) => boolean;
};

const sleep = (ms: number) => new Promise((resolve) => setTimeout(resolve, ms));`,
  },
  {
    label: "Approach",
    body: "A loop over attempts. Try, and on failure decide two things separately: is this error retryable at all, and are there attempts left. If both, sleep and go again. Otherwise rethrow the error you actually got, not a generic one.",
    code: `let lastError: unknown;
for (let attempt = 1; attempt <= attempts; attempt++) {
  try {
    return await operation();
  } catch (error) {
    lastError = error;
    if (!isRetryable(error)) throw error;   // a 400 fails the same way forever
    if (attempt === attempts) break;        // break, not sleep: no delay after the last try
    await sleep(baseDelayMs * 2 ** (attempt - 1));
  }
}
throw lastError;   // the real failure, so the caller keeps the diagnosis`,
  },
  {
    label: "The trap",
    body: "The exponent is attempt minus one, so the first retry waits the base delay rather than double it. And attempts of 3 is three calls in total, not three retries on top of the first. Nothing catches either one except a test that asserts the actual timings, which is why both are in the hidden cases.",
  },
];

export const retryBackoff: Drill = {
  id: "retry-backoff",
  track: "typescript",
  title: "Retry with exponential backoff",
  pattern: "async-retry",
  difficulty: "medium",
  basePoints: 250,
  timeBudgetMs: 25 * 60 * 1000,
  prelude: FAKE_CLOCK_PRELUDE,
  statement: `A payment API fails perhaps one call in fifty, for no reason you can control. Write \`retry\`, which runs an operation again on failure, waiting longer each time.

\`\`\`ts
type RetryOptions = {
  attempts: number;            // total tries, including the first
  baseDelayMs: number;         // the wait before the first retry
  isRetryable?: (error: unknown) => boolean;   // default: retry everything
};

async function retry<T>(operation: () => Promise<T>, options: RetryOptions): Promise<T>
\`\`\`

## Requirements

- **\`attempts\` is the total number of tries**, so \`attempts: 3\` means one call and at most two retries.
- **Delays double**: \`base\`, \`base * 2\`, \`base * 4\`. The first retry waits exactly \`base\`.
- **\`isRetryable\` is consulted first.** If it says no, throw straight away without waiting.
- **The error thrown is the last one seen**, not a generic wrapper.
- **No delay after the final attempt.**

## Example

\`\`\`ts
const charge = await retry(() => api.charge(order), {
  attempts: 4,
  baseDelayMs: 200,
  isRetryable: (e) => (e as HttpError).status >= 500,
});
// calls at t=0, 200, 600, 1400 — and a 400 stops it dead at the first attempt
\`\`\`

## Where this gets interesting

Two failures cost real money in production.

**Retrying what cannot succeed.** A 400 is going to fail the same way every time. Retrying it burns the budget, delays the error the caller needs, and multiplies load on a service that already told you no.

**The off-by-one.** "Three retries" and "three attempts" are different, and the exponent is \`attempt - 1\` so the first retry waits the base delay rather than double it. Nothing catches either one except a test that asserts the actual timings.

\`setTimeout\` and \`Date.now\` are on a fake clock here, so the tests are instant. Write it as you normally would.`,
  hints,
  starter,
  harness,
  solution,
  complexity:
    "O(attempts) calls, with total waiting of base * (2^(attempts-1) - 1). Worth stating that bound out loud: it is what makes an aggressive attempts value dangerous.",
};
